import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/providers/api-client';

// ============================================
// Types
// ============================================

export interface DashboardSummary {
  totalUsers: number;
  activeSubscriptions: number;
  confirmedRevenue: number;
  baseCurrency: string;
  onlineUsersCount: number;
  newUsersInPeriod: number;
  revenueChange: number;
}

export interface OnlineUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  lastActivity: string;
}

export interface GeoDistributionItem {
  country: string;
  countryCode: string;
  users: number;
  percentage: number;
}

export interface TopCourseTierBreakdown {
  tierId: number | null;
  tierLabel: string;
  durationDays: number | null;
  purchases: number;
  revenue: number;
}

export interface TopCourseItem {
  id: number;
  title: string;
  thumbnailUrl: string | null;
  views: number;
  purchases: number;
  revenue: number;
  targetType?: string;
  tiers?: TopCourseTierBreakdown[];
}

export interface RevenueDataPoint {
  date: string;
  amount: number;
  count: number;
}

export interface DashboardPeriod {
  type: 'week' | 'month' | 'year' | 'custom';
  startDate: string;
  endDate: string;
}

export interface DashboardData {
  summary: DashboardSummary;
  charts: {
    revenue: RevenueDataPoint[];
  };
  geography: GeoDistributionItem[];
  topCourses: TopCourseItem[];
  onlineUsers: OnlineUser[];
  period: DashboardPeriod;
}

export interface OnlineUsersData {
  count: number;
  users: OnlineUser[];
}

export interface GeographyData {
  distribution: GeoDistributionItem[];
  totalCountries: number;
}

export interface TopCoursesData {
  courses: TopCourseItem[];
  period: DashboardPeriod;
}

export interface ConfirmedRevenueData {
  total: number;
  totalCount: number;
  byDay: RevenueDataPoint[];
  period: DashboardPeriod;
}

export interface AnalyticsCurrencyData {
  currency: string;
  availableCurrencies: string[];
}

// ============================================
// API Functions
// ============================================

async function fetchDashboard(
  period: string,
  startDate?: string,
  endDate?: string
): Promise<DashboardData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  const response = await apiClient.get<DashboardData>(`/analytics/dashboard?${params}`);
  return response;
}

async function fetchOnlineUsers(limit = 50): Promise<OnlineUsersData> {
  const response = await apiClient.get<OnlineUsersData>(
    `/analytics/online-users?limit=${limit}`
  );
  return response;
}

async function fetchGeography(days = 30): Promise<GeographyData> {
  const response = await apiClient.get<GeographyData>(
    `/analytics/geography?days=${days}`
  );
  return response;
}

async function fetchTopCourses(
  limit: number,
  period: string,
  startDate?: string,
  endDate?: string
): Promise<TopCoursesData> {
  const params = new URLSearchParams({ limit: String(limit), period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  const response = await apiClient.get<TopCoursesData>(
    `/analytics/top-courses?${params}`
  );
  return response;
}

async function fetchConfirmedRevenue(
  period: string,
  startDate?: string,
  endDate?: string
): Promise<ConfirmedRevenueData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}

  const response = await apiClient.get<ConfirmedRevenueData>(
    `/analytics/confirmed-revenue?${params}`
  );
  return response;
}

async function fetchAnalyticsCurrency(): Promise<AnalyticsCurrencyData> {
  const response = await apiClient.get<AnalyticsCurrencyData>(
    '/admin/system-settings/analytics-currency'
  );
  return response;
}

async function updateAnalyticsCurrency(currency: string): Promise<{ currency: string; message: string }> {
  const response = await apiClient.put<{ currency: string; message: string }>(
    '/admin/system-settings/analytics-currency',
    { currency }
  );
  return response;
}

// ============================================
// Hooks
// ============================================

/**
 * Хук для получения данных дашборда
 */
export function useDashboardData(
  period: string = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['dashboard', period, startDate, endDate],
    queryFn: () => fetchDashboard(period, startDate, endDate),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения онлайн пользователей
 */
export function useOnlineUsers(limit = 50) {
  return useQuery({
    queryKey: ['online-users', limit],
    queryFn: () => fetchOnlineUsers(limit),
    staleTime: 30 * 1000, // 30 секунд
    refetchInterval: 30 * 1000, // Автообновление каждые 30 секунд
  });
}

/**
 * Хук для получения географии пользователей
 */
export function useGeography(days = 30) {
  return useQuery({
    queryKey: ['geography', days],
    queryFn: () => fetchGeography(days),
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения топ курсов
 */
export function useTopCourses(
  limit = 10,
  period = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['top-courses', limit, period, startDate, endDate],
    queryFn: () => fetchTopCourses(limit, period, startDate, endDate),
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения подтвержденной выручки
 */
export function useConfirmedRevenue(
  period = 'month',
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['confirmed-revenue', period, startDate, endDate],
    queryFn: () => fetchConfirmedRevenue(period, startDate, endDate),
    staleTime: 60 * 1000, // 1 минута
    refetchOnWindowFocus: false,
  });
}

/**
 * Хук для получения текущей валюты аналитики
 */
export function useAnalyticsCurrency() {
  return useQuery({
    queryKey: ['analytics-currency'],
    queryFn: fetchAnalyticsCurrency,
    staleTime: 10 * 60 * 1000, // 10 минут
    refetchOnWindowFocus: false,
  });
}

/**
 * Функция для обновления валюты аналитики
 */
export { updateAnalyticsCurrency };

// ============================================
// Phase 6 — Extended widgets
// ============================================

export interface FunnelStep {
  name: string;
  count: number;
  /** Конверсия относительно первого шага воронки (0..100). */
  rate: number;
}

export interface FunnelData {
  steps: FunnelStep[];
}

async function fetchFunnel(
  period: string,
  startDate?: string,
  endDate?: string,
): Promise<FunnelData> {
  const params = new URLSearchParams({ period });
  if (startDate) {params.append('startDate', startDate);}
  if (endDate) {params.append('endDate', endDate);}
  return apiClient.get<FunnelData>(`/analytics/funnel?${params}`);
}

/**
 * Воронка конверсии dashboard: visitor → registered → first purchase → repeat.
 */
export function useFunnel(
  period: string = 'month',
  startDate?: string,
  endDate?: string,
) {
  return useQuery({
    queryKey: ['analytics-funnel', period, startDate, endDate],
    queryFn: () => fetchFunnel(period, startDate, endDate),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface CohortRetentionRow {
  month: string;
  size: number;
  /** Retention rates per month offset (0..5), в процентах. */
  retention: number[];
}

export interface CohortRetentionData {
  cohorts: CohortRetentionRow[];
}

async function fetchCohortRetention(months: number): Promise<CohortRetentionData> {
  const params = new URLSearchParams({ months: String(months) });
  return apiClient.get<CohortRetentionData>(`/analytics/cohort-retention?${params}`);
}

/**
 * Когортное удержание: матрица retention rates.
 * Rows — месяцы первой оплаты юзера, cols — смещение в месяцах.
 */
export function useCohortRetention(months = 12) {
  return useQuery({
    queryKey: ['analytics-cohort-retention', months],
    queryFn: () => fetchCohortRetention(months),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface MrrChurnPoint {
  month: string;
  mrr: number;
  churnRate: number;
  activeCount: number;
}

export interface MrrChurnData {
  months: MrrChurnPoint[];
}

async function fetchMrrChurn(months: number): Promise<MrrChurnData> {
  const params = new URLSearchParams({ months: String(months) });
  return apiClient.get<MrrChurnData>(`/analytics/mrr-churn?${params}`);
}

/**
 * MRR (monthly recurring revenue) и churn rate по месяцам.
 */
export function useMrrChurn(months = 12) {
  return useQuery({
    queryKey: ['analytics-mrr-churn', months],
    queryFn: () => fetchMrrChurn(months),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface TierDistributionSlice {
  tierId: number | null;
  label: string;
  count: number;
  percentage: number;
}

export interface TierDistributionData {
  tiers: TierDistributionSlice[];
}

async function fetchTierDistribution(): Promise<TierDistributionData> {
  return apiClient.get<TierDistributionData>('/analytics/tier-distribution');
}

/**
 * Распределение активных подписок по тарифам.
 */
export function useTierDistribution() {
  return useQuery({
    queryKey: ['analytics-tier-distribution'],
    queryFn: fetchTierDistribution,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface HeatmapCell {
  /** 0 = воскресенье, 1 = понедельник, ..., 6 = суббота (Postgres DOW). */
  dayOfWeek: number;
  hour: number;
  count: number;
}

export interface ActiveHeatmapData {
  cells: HeatmapCell[];
}

async function fetchActiveHeatmap(days: number): Promise<ActiveHeatmapData> {
  const params = new URLSearchParams({ days: String(days) });
  return apiClient.get<ActiveHeatmapData>(`/analytics/active-heatmap?${params}`);
}

/**
 * Heatmap активности: события UserAction по часам и дням недели.
 */
export function useActiveHeatmap(days = 7) {
  return useQuery({
    queryKey: ['analytics-active-heatmap', days],
    queryFn: () => fetchActiveHeatmap(days),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface TopPageItem {
  page: string;
  views: number;
  uniqueVisitors: number;
}

export interface TopPagesData {
  pages: TopPageItem[];
}

export type TopPageEventsType = 'views' | 'visitors';

export interface TopPageEventUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

export interface TopPageEventRecord {
  id: number;
  sessionId: number;
  userId: number | null;
  type: string;
  page: string;
  createdAt: string;
  user: TopPageEventUser | null;
}

async function fetchTopPages(days: number, limit: number): Promise<TopPagesData> {
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  return apiClient.get<TopPagesData>(`/analytics/top-pages?${params}`);
}

/**
 * Топ-страницы по просмотрам из UserAction.
 */
export function useTopPages(days = 30, limit = 10) {
  return useQuery({
    queryKey: ['analytics-top-pages', days, limit],
    queryFn: () => fetchTopPages(days, limit),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

async function fetchTopPageEvents(
  targetPage: string,
  type: TopPageEventsType,
  days: number,
  page: number,
  limit: number,
): Promise<TopPageEventRecord[]> {
  const params = new URLSearchParams({
    targetPage,
    type,
    days: String(days),
    page: String(page),
    limit: String(limit),
  });
  return apiClient.get<TopPageEventRecord[]>(`/analytics/top-pages/events?${params}`);
}

export function useTopPageEvents(
  targetPage: string | null,
  type: TopPageEventsType | null,
  days: number,
  page: number,
  limit: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['analytics-top-page-events', targetPage, type, days, page, limit],
    queryFn: () => fetchTopPageEvents(targetPage ?? '', type ?? 'views', days, page, limit),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: enabled && targetPage !== null && type !== null,
  });
}

// ============================================
// Online History
// ============================================

export interface OnlineHistoryPoint {
  timestamp: number;
  count: number;
}

async function fetchOnlineHistory(hours = 24): Promise<{ data: OnlineHistoryPoint[] }> {
  const response = await apiClient.get<{ data: OnlineHistoryPoint[] }>(
    `/analytics/chart/online?hours=${hours}`
  );
  return response;
}

/**
 * Хук для получения истории онлайн-пользователей (спарклайн)
 */
export function useOnlineHistory(hours = 24) {
  return useQuery({
    queryKey: ['online-history', hours],
    queryFn: () => fetchOnlineHistory(hours),
    staleTime: 5 * 60 * 1000, // 5 минут
    refetchInterval: 5 * 60 * 1000,
  });
}

// ============================================
// Presence Timeline (Gantt-style history)
// ============================================

export type PresenceSource = 'WEB' | 'BOT' | 'MINI_APP';

export interface PresenceTimelineInterval {
  /** ISO datetime — начало интервала (может быть до окна запроса). */
  start: string;
  /** ISO datetime либо null, если интервал ещё открыт (юзер онлайн сейчас). */
  end: string | null;
  /** Источник heartbeat: WEB / BOT / MINI_APP. */
  source: PresenceSource;
  /** UserSession id, если источник его знал. */
  sessionId: number | null;
}

export interface PresenceTimelineUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

export interface PresenceTimelineEntry {
  user: PresenceTimelineUser;
  intervals: PresenceTimelineInterval[];
}

export interface PresenceTimelineResponse {
  users: PresenceTimelineEntry[];
}

async function fetchPresenceTimeline(
  from: string,
  to: string,
  userId?: number,
  limit?: number,
): Promise<PresenceTimelineResponse> {
  const params = new URLSearchParams({ from, to });
  if (userId !== undefined) {
    params.append('userId', String(userId));
  }
  if (limit !== undefined) {
    params.append('limit', String(limit));
  }
  return apiClient.get<PresenceTimelineResponse>(
    `/analytics/presence/timeline?${params.toString()}`,
  );
}

/**
 * Хук для Gantt-таймлайна онлайна пользователей.
 * @param fromISO — ISO-строка начала окна
 * @param toISO — ISO-строка конца окна
 * @param userId — опциональный фильтр по одному юзеру
 */
export function usePresenceTimeline(
  fromISO: string,
  toISO: string,
  userId?: number,
  limit = 100,
) {
  return useQuery({
    queryKey: ['presence-timeline', fromISO, toISO, userId, limit],
    queryFn: () => fetchPresenceTimeline(fromISO, toISO, userId, limit),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: Boolean(fromISO && toISO),
  });
}

// ─── Expiring Subscriptions ───────────────────────────────────────────────────

export interface ExpiringSubscriptionUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  telegramId: number | null;
}

export interface ExpiringSubscriptionPlan {
  id: number;
  code: string;
  durationDays: number | null;
}

export interface ExpiringSubscriptionItem {
  id: number;
  userId: number;
  endDate: string;
  renewalCount: number;
  user: ExpiringSubscriptionUser | null;
  subscriptionPlan: ExpiringSubscriptionPlan | null;
}

export interface ExpiringSubscriptionsData {
  data: ExpiringSubscriptionItem[];
  days: number;
}

async function fetchExpiringSoon(days: number): Promise<ExpiringSubscriptionsData> {
  return apiClient.get<ExpiringSubscriptionsData>(`/subscriptions/expiring?days=${days}`);
}

export function useExpiringSoon(days = 7) {
  return useQuery({
    queryKey: ['expiring-subscriptions', days],
    queryFn: () => fetchExpiringSoon(days),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
